import React, { useEffect, useRef, useState } from "react";
import { Columns3, Check } from "lucide-react";
import { cn } from "../../lib/utils";

// Show / hide columns of the list view (remembered per table on this device).
export default function ColumnPicker({ columns, hidden, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };  // capture: keep the drawer open
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc, true);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc, true); };
  }, [open]);
  const toggle = (k) => {
    const next = new Set(hidden);
    if (next.has(k)) next.delete(k); else if (columns.length - next.size > 1) next.add(k);
    onChange(next);
  };
  const shown = columns.length - hidden.size;
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid="dt-columns-toggle" onClick={() => setOpen(o => !o)}
              className={cn("inline-flex items-center gap-1.5 h-8 px-3 border rounded-sm text-[11.5px] transition-colors",
                open ? "border-primary text-primary" : "border-border hover:border-primary")}>
        <Columns3 size={12} /> Columns <span className="font-mono-tabular text-muted">{shown}/{columns.length}</span>
      </button>
      {open && (
        <div data-testid="dt-columns-menu" className="fixed left-3 right-3 sm:absolute sm:left-auto sm:right-0 sm:w-64 mt-1 max-h-[360px] overflow-y-auto bg-surface border border-border rounded-sm shadow-2xl z-30 py-1">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border text-[10.5px]">
            <button type="button" data-testid="dt-columns-all" onClick={() => onChange(new Set())} className="text-primary hover:underline">Show all</button>
            <span className="text-muted">At least one stays visible</span>
          </div>
          {columns.map(c => {
            const on = !hidden.has(c.key);
            return (
              <button type="button" key={c.key} data-testid={`dt-col-option-${c.key}`} onClick={() => toggle(c.key)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11.5px] hover:bg-surface-2">
                <span className={cn("w-3.5 h-3.5 border rounded-[2px] inline-flex items-center justify-center shrink-0",
                  on ? "bg-primary border-primary text-primary-fg" : "border-border")}>{on && <Check size={10} />}</span>
                <span className="font-mono-tabular truncate">{c.key}</span>
                {c.pii && <span className="ml-auto text-[9px] uppercase tracking-widest text-warning">PII</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
